import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

export interface GoogleAdsDatePeriod {
  start_date: string;
  end_date: string;
}

export interface GoogleAdsPaginationMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface GoogleAdsKpiItem {
  id: string;
  title: string;
  value: number;
  formatted_value: string;
  previous_value?: number;
  percent_change?: number | null;
  /** Localized change string, e.g. "11.83%" / "١١.٨٣%". */
  percent_changeFormattedValue?: string;
  /** Legacy alias some backends may still return */
  change_percent?: number | null;
}

export interface GoogleAdsKpiResponse {
  period?: GoogleAdsDatePeriod;
  comparison_period?: GoogleAdsDatePeriod;
  kpis: GoogleAdsKpiItem[];
}

export interface GoogleAdsPerformanceTrendItem {
  date?: string;
  report_date?: string;
  day?: string;
  total_spend?: number;
  spend?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
}

export interface GoogleAdsPerformanceTrendsResponse {
  performance_trends: GoogleAdsPerformanceTrendItem[];
  performance_trends_monthly?: GoogleAdsPerformanceTrendItem[];
}

export interface GoogleAdsRoasByCampaignItem {
  campaign_id?: string;
  campaign_name: string;
  roas: number;
}

export interface GoogleAdsSpendByCampaignItem {
  campaign_id?: string;
  campaign_name: string;
  spend: number;
  spendFormattedValue?: string;
}

export interface GoogleAdsRoasByCampaignResponse {
  roas_by_campaign: GoogleAdsRoasByCampaignItem[];
  spend_by_campaign: GoogleAdsSpendByCampaignItem[];
}

export interface GoogleAdsPerformanceMetrics {
  interactions?: number;
  spend?: number;
  impressions?: number;
  clicks?: number;
  ctr?: number;
  cpc?: number;
  cpa?: number;
  conversions?: number;
  roas?: number;
}

export interface GoogleAdsAdGroupItem extends GoogleAdsPerformanceMetrics {
  ad_group_id?: string;
  ad_group_name: string;
  ad_network_type?: string;
  start_date?: string;
  end_date?: string;
}

export interface GoogleAdsCampaignItem extends GoogleAdsPerformanceMetrics {
  campaign_id?: string;
  campaign_name: string;
  ad_network_type?: string;
  start_date?: string;
  end_date?: string;
  budgetFormattedValue?: string;
  ad_groups?: GoogleAdsAdGroupItem[];
}

export type GoogleAdsCampaignSort =
  | "campaign_name_desc"
  | "campaign_name_asc"
  | "ad_network_type_desc"
  | "ad_network_type_asc"
  | "start_date_desc"
  | "start_date_asc"
  | "end_date_desc"
  | "end_date_asc"
  | "interactions_desc"
  | "interactions_asc"
  | "spend_desc"
  | "spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "cpc_desc"
  | "cpc_asc"
  | "cpa_desc"
  | "cpa_asc"
  | "conversions_desc"
  | "conversions_asc"
  | "roas_desc"
  | "roas_asc";

export type GoogleAdsAdGroupSort =
  | "ad_group_name_desc"
  | "ad_group_name_asc"
  | "ad_network_type_desc"
  | "ad_network_type_asc"
  | "start_date_desc"
  | "start_date_asc"
  | "end_date_desc"
  | "end_date_asc"
  | "interactions_desc"
  | "interactions_asc"
  | "spend_desc"
  | "spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "cpc_desc"
  | "cpc_asc"
  | "cpa_desc"
  | "cpa_asc"
  | "conversions_desc"
  | "conversions_asc"
  | "roas_desc"
  | "roas_asc";

export interface GoogleAdsPaginatedListParams {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
  page?: number;
  page_size?: number;
  sort?: GoogleAdsCampaignSort | string | null;
  search?: string | null;
  ad_network_type?: string | null;
}

export interface GoogleAdsAdGroupListParams {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
  page?: number;
  page_size?: number;
  sort?: GoogleAdsAdGroupSort | string | null;
  search?: string | null;
}

export interface GoogleAdsCampaignsResponse {
  meta: GoogleAdsPaginationMeta;
  campaigns: GoogleAdsCampaignItem[];
}

export interface GoogleAdsAdGroupsResponse {
  meta: GoogleAdsPaginationMeta;
  ad_groups: GoogleAdsAdGroupItem[];
}

export interface GoogleAdsAdNetworkTypesResponse {
  ad_network_types: string[];
}

export interface GoogleAdsAdGroupChartPerformanceItem {
  ad_group_id?: string;
  ad_group_name: string;
  clicks: number;
  conversions: number;
}

export interface GoogleAdsSpendByAdGroupItem {
  ad_group_id?: string;
  ad_group_name: string;
  spend: number;
}

export interface GoogleAdsAdGroupChartsResponse {
  ad_group_performance: GoogleAdsAdGroupChartPerformanceItem[];
  spend_by_ad_group: GoogleAdsSpendByAdGroupItem[];
}

export interface GoogleAdsAdNetworkBreakdownItem {
  ad_network_type: string;
  clicks: number;
  conversions: number;
}

export interface GoogleAdsAdNetworkBreakdownResponse {
  ad_network_breakdown: GoogleAdsAdNetworkBreakdownItem[];
}

export interface GoogleAdsPerformanceByAdItem {
  campaign_id?: string;
  campaign_name: string;
  ad_id: string;
  ad_network_type?: string;
  interactions?: number;
  total_spend?: number;
  total_spendFormattedValue?: string;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  ctr?: number;
  cpc?: number;
  cpcFormattedValue?: string;
  cpa?: number;
  cpaFormattedValue?: string;
  roas?: number;
}

export type GoogleAdsAdsSort =
  | "campaign_name_desc"
  | "campaign_name_asc"
  | "ad_id_desc"
  | "ad_id_asc"
  | "ad_network_type_desc"
  | "ad_network_type_asc"
  | "interactions_desc"
  | "interactions_asc"
  | "spend_desc"
  | "spend_asc"
  | "impressions_desc"
  | "impressions_asc"
  | "clicks_desc"
  | "clicks_asc"
  | "conversions_desc"
  | "conversions_asc"
  | "ctr_desc"
  | "ctr_asc"
  | "cpc_desc"
  | "cpc_asc"
  | "cpa_desc"
  | "cpa_asc"
  | "roas_desc"
  | "roas_asc";

export interface GoogleAdsAdsListParams {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
  page?: number;
  page_size?: number;
  sort?: GoogleAdsAdsSort | string | null;
  search?: string | null;
}

export interface GoogleAdsPerformanceByAdResponse {
  meta?: GoogleAdsPaginationMeta;
  ads: GoogleAdsPerformanceByAdItem[];
}

type GoogleAdsDashboardDateParams = {
  start_date: string;
  end_date: string;
  lang?: ApiLang | string | null;
};

const buildDateSearchParams = (params: GoogleAdsDashboardDateParams) =>
  new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
    lang: normalizeApiLang(params.lang),
  });

const appendPaginatedParams = (
  searchParams: URLSearchParams,
  params: { page?: number; page_size?: number; sort?: string | null; search?: string | null }
) => {
  if (params.page !== undefined) {
    searchParams.set("page", String(params.page));
  }
  if (params.page_size !== undefined) {
    searchParams.set("page_size", String(params.page_size));
  }
  if (params.sort) {
    searchParams.set("sort", params.sort);
  }
  if (params.search?.trim()) {
    searchParams.set("search", params.search.trim());
  }
};

const appendOptionalListParams = (
  searchParams: URLSearchParams,
  params: GoogleAdsPaginatedListParams
) => {
  appendPaginatedParams(searchParams, params);
  if (params.ad_network_type?.trim()) {
    searchParams.set("ad_network_type", params.ad_network_type.trim());
  }
};

const fetchGoogleAdsDashboard = async <T,>(
  path: string,
  params: GoogleAdsDashboardDateParams
): Promise<T> => {
  const response = await fetch(
    `${API_BASE_URL}/google-ads/dashboard/${path}?${buildDateSearchParams(params).toString()}`,
    {
      method: "GET",
      headers: buildAuthHeaders("api_key"),
    }
  );

  return handleJsonResponse<T>(response);
};

export const googleAdsDashboardService = {
  fetchDashboardKPI: async (
    params: GoogleAdsDashboardDateParams
  ): Promise<GoogleAdsKpiResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/google-ads/dashboard/kpi?${buildDateSearchParams(params).toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<GoogleAdsKpiResponse>(response);
  },

  fetchPerformanceTrends: (params: GoogleAdsDashboardDateParams) =>
    fetchGoogleAdsDashboard<GoogleAdsPerformanceTrendsResponse>("performance-trends", params),

  fetchRoasByCampaign: (params: GoogleAdsDashboardDateParams) =>
    fetchGoogleAdsDashboard<GoogleAdsRoasByCampaignResponse>("roas-by-campaign", params),

  fetchCampaigns: async (
    params: GoogleAdsPaginatedListParams
  ): Promise<GoogleAdsCampaignsResponse> => {
    const searchParams = buildDateSearchParams(params);
    appendOptionalListParams(searchParams, params);

    const response = await fetch(
      `${API_BASE_URL}/google-ads/dashboard/campaigns?${searchParams.toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<GoogleAdsCampaignsResponse>(response);
  },

  fetchAdNetworkTypes: (params: GoogleAdsDashboardDateParams) =>
    fetchGoogleAdsDashboard<GoogleAdsAdNetworkTypesResponse>("ad-network-types", params),

  fetchAdGroupPerformance: async (
    params: GoogleAdsAdGroupListParams
  ): Promise<GoogleAdsAdGroupsResponse> => {
    const searchParams = buildDateSearchParams(params);
    appendPaginatedParams(searchParams, params);

    const response = await fetch(
      `${API_BASE_URL}/google-ads/dashboard/ad-group-performance?${searchParams.toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<GoogleAdsAdGroupsResponse>(response);
  },

  fetchAdGroupCharts: (params: GoogleAdsDashboardDateParams) =>
    fetchGoogleAdsDashboard<GoogleAdsAdGroupChartsResponse>("ad-group-charts", params),

  fetchAdNetworkBreakdown: (params: GoogleAdsDashboardDateParams) =>
    fetchGoogleAdsDashboard<GoogleAdsAdNetworkBreakdownResponse>("ad-network-breakdown", params),

  fetchPerformanceByAd: async (
    params: GoogleAdsAdsListParams
  ): Promise<GoogleAdsPerformanceByAdResponse> => {
    const searchParams = buildDateSearchParams(params);
    appendPaginatedParams(searchParams, params);

    const response = await fetch(
      `${API_BASE_URL}/google-ads/dashboard/performance-by-ad?${searchParams.toString()}`,
      {
        method: "GET",
        headers: buildAuthHeaders("api_key"),
      }
    );

    return handleJsonResponse<GoogleAdsPerformanceByAdResponse>(response);
  },
};
